import { Download } from 'lucide-react';
import { useState, type MouseEvent } from 'react';
import { download } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Button, useToast, type ButtonProps } from './ui';

/**
 * Downloads a document the API draws — an invoice or a receipt PDF — in the
 * language the app is showing, so what prints matches what is on screen.
 * `locale={null}` leaves the language to the API: a contract comes in the one
 * it is written in. It never lets the click through: payment rows navigate
 * when clicked.
 */
export function DownloadButton({
  path,
  locale: language,
  children,
  ...props
}: { path: string; locale?: 'en' | 'ar' | null } & Omit<ButtonProps, 'onClick' | 'loading' | 'icon'>) {
  const { locale: appLocale } = useI18n();
  const locale = language === undefined ? appLocale : language;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const onClick = async (event: MouseEvent) => {
    event.stopPropagation();
    setBusy(true);
    try {
      await download(path, locale ? { locale } : undefined);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button {...props} loading={busy} icon={<Download className="h-4 w-4" aria-hidden />} onClick={onClick}>
      {children}
    </Button>
  );
}
